import { useEffect, useState } from 'react';

type UseCloudReachabilityOptions = {
  enabled?: boolean;
  intervalMs?: number;
  timeoutMs?: number;
};

// The stand-in page and the cloud app live on different origins, so we cannot read the
// probe response. A tolerant `no-cors` fetch is enough: any resolved (opaque) response
// means the cloud origin answered and is reachable; an abort/timeout or network error
// means it is not. We never inspect the body or status — only whether the request settled.
export async function probeCloudReachable(url: string, timeoutMs: number): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    await fetch(url, { mode: 'no-cors', cache: 'no-store', signal: controller.signal });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Polls whether the cloud origin is actually reachable from a stand-in (offline-mode)
 * session. `networkStatus === 'ONLINE'` only means the local LAN is up; on an isolated
 * facility network the cloud can be dead while the LAN is fine. This probes the real
 * origin on a modest interval so callers can gate "Return to Cloud" on live reachability.
 *
 * Defaults: probe every 15s, abort each probe after 5s.
 */
export function useCloudReachability(
  url: string | undefined,
  { enabled = true, intervalMs = 15000, timeoutMs = 5000 }: UseCloudReachabilityOptions = {}
): boolean {
  const [reachable, setReachable] = useState(false);

  useEffect(() => {
    if (!enabled || !url) {
      setReachable(false);
      return undefined;
    }

    let cancelled = false;

    const runProbe = async () => {
      const result = await probeCloudReachable(url, timeoutMs);
      if (!cancelled) {
        setReachable(result);
      }
    };

    runProbe();
    const interval = setInterval(runProbe, intervalMs);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [url, enabled, intervalMs, timeoutMs]);

  return reachable;
}

export default useCloudReachability;
